import _ from "underscore";
import DriveView from "../../DriveView";
import { DispatcherEvents, dispatcher } from "../../utils/dispatcher";
import ToggleSwitch from "../framework/toggleSwitch";
import User from "../../model/User";
import { MobileActions } from "../../store/mobile/actions";
import Spinner from "../../utils/spinner";
import Sync from "../../sync/sync";
import { BaseCache, makeDeviceCache } from "../../utils/storage/cache";
import { getCurrentVehicle, isNoVehicle } from "../../utils/device";
import { navigateToAssetSelection } from "../../utils/native/nativeAssetSelection";
import { updateAppCache } from "../../utils/appcache";
import serviceWorker from "../../utils/serviceWorker";
import Session from "../../utils/session";
import Network from "../../services/network";
import { hide, show, htmlEscape } from "../../utils/vanilla";
import { handleApi } from "../../utils/error";
import pageHTML from "./main.tmpl";
import { DataActions } from "../../store/data/actions";
import { HistoricalDayActions } from "../../store/hosLogs/actions";
import { FeatureFlagName } from "../../utils/featureFlagName";
import { Device } from "../../model/Device";
import { collectCustomAddins } from "../../utils/pageLoader";
import { setPipMode, shouldSetPiP } from "../../utils/native/nativeHelpers";
import { IDataStore, ServerFlags } from "../../store/data/types";

const darkModeAllowedWhitelabels = [
    "spireon",
    "transflo",
    "smartdrive"
];

interface IAddinLink {
    id: string;
    name: string;
    imgUrl: string;
}

class DoneChecking {
    sync = false;
    appCache = false;
    vehicleCache = false;
    systemSettings = false;
    clockDrift = false;
    featureFlags = false;
    checkingForUpdates: boolean;
}

let complete = new DoneChecking();
let spin;

export default class OtherMenuView extends DriveView<Backbone.Model> {
    private readonly template: any = null;
    private readonly currentUser = new User(this.app.userService.getCurrentUser());
    private readonly toggleSwitch;
    private readonly isDarkModeAllowed = !window.whitelabel || darkModeAllowedWhitelabels.some(wl => wl === window.whitelabel);
    private readonly togglePipSwitch;
    private readonly isPipModeAllowed = !window.whitelabel;

    private readonly sync: Sync;
    private readonly session: Session;
    private readonly network: Network;
    private readonly doneChecking: DoneChecking;
    private readonly deviceCache: BaseCache<Device | IDevice>;
    private cacheUpdate: (() => void) | null;
    private cacheProgress: ((e) => void) | null;

    constructor (options) {
        super(options, {
            title: "Other",
            activeSection: "other",
            pageLevel: 1
        });
        this.template = _.template(pageHTML);

        this.sync = this.app.sync;
        this.session = this.app.session;
        this.network = this.app.network;
        this.deviceCache = makeDeviceCache(this.app.store);
        if (this.isDarkModeAllowed) {
            this.toggleSwitch = new ToggleSwitch({
                labelId: "darkmode-toggle-label",
                switchId: "darkmode-toggle",
                onText: "On",
                offText: "Off",
                ariaLabel: "Dark mode toggle",
                isEnabled: this.app.store.getState().data.nightMode
            });
        }
        if (this.isPipModeAllowed) {
            this.togglePipSwitch = new ToggleSwitch({
                labelId: "pipmode-toggle-label",
                switchId: "pipmode-toggle",
                onText: "On",
                offText: "Off",
                ariaLabel: "Pip mode toggle",
                isEnabled: this.app.store.getState().data.pipMode
            });
        }
        if (options.doneChecking) {
            this.doneChecking = options.doneChecking;
        } else {
            this.doneChecking = complete;
        }

        this.listenTo(dispatcher, DispatcherEvents.UserUpdate, this.onUserUpdate.bind(this));
        this.listenTo(dispatcher, DispatcherEvents.SystemSettingsUpdate, this.render.bind(this));
    }

    /* eslint-disable @typescript-eslint/unbound-method */
    events() {
        return {
            "click #checkForUpdates": this.checkForUpdates,
            "click a.menu-link": this.navigateToPage,
            "click a.menu-addin": this.navigateAddin,
            "change input#darkmode-toggle": this.toggleDarkMode,
            "change input#pipmode-toggle": this.togglePipMode,
            "click #logout": this.toLogoutWorkflow
        } as Backbone.EventsHash;
    }
    /* eslint-enable @typescript-eslint/unbound-method */

    render() {
        let hasNoVehicle = isNoVehicle();
        let canAccessHos = this.app.securityService.can("useHos") || (this.app.securityService.can("attemptHos") && hasNoVehicle);
        let canAccessDvir = this.app.securityService.can("viewDvir") && !hasNoVehicle;
        let canAccessTrips = this.app.securityService.can("showTrips");
        let shouldShowTrips = canAccessTrips && !document.getElementById("menu-trips");
        let canSeeInspectionMode = (canAccessHos || canAccessDvir) && this.app.store.getState().data.ELDJurisdiction !== "EWD";
        let canSeePipSettings = this.app.featureFlagService.hasFeatureFlag(FeatureFlagName.PIP_FUNCTIONALITY) && shouldSetPiP();
        let addins = this.getFormattedAddins();

        const dataState: DeepReadonlyObject<IDataStore> = this.app.store.getState().data;
        const isFedRamp = dataState.serverFlags === ServerFlags.None;

        let canSeeEmailLogs = canSeeInspectionMode && this.app.securityService.can("emailLogs") && !isFedRamp;

        this.display(this.template({
            addins,
            debugMode: this.app.store.getState().data.debugMode,
            isDarkModeAllowed: this.isDarkModeAllowed,
            isPipModeAllowed: this.isPipModeAllowed && canSeePipSettings,
            darkModeToggleSwitch: this.isDarkModeAllowed && this.toggleSwitch.create(),
            pipModeToggleSwitch: this.isPipModeAllowed && this.togglePipSwitch.create(),
            currentUserName: this.currentUser.getFullName() || "",
            shouldShowTrips,
            canSeeInspectionMode,
            canSeeEmailLogs
        })).then(() => {
            this.checkIfDoneUpdating();
            if (this.doneChecking.checkingForUpdates) {
                spin = new Spinner().spin(this.el.querySelector("#settings-update-spinner"));
                let checkForUpdatesElement = document.getElementById("checkForUpdates") as HTMLInputElement;
                if (checkForUpdatesElement) {
                    checkForUpdatesElement.classList.add("disabled");
                }

                this.cacheUpdate = () => {
                    // No update or update ready
                    this.doneChecking.appCache = true;
                    this.checkIfDoneUpdating();
                };
            }
        })
        .catch(handleApi);
        return this;
    }

    updateCacheStatus(value = "0%") {
        let progress = <HTMLDivElement>document.getElementById("settings-update-progress");
        if (progress) {
            show(progress);

            let textBar = <HTMLSpanElement>progress.querySelector("span");
            if (textBar) {
                textBar.innerHTML = value;
            }
        }
    }

    resetDoneChecking() {
        this.doneChecking.sync = false;
        this.doneChecking.appCache = false;
        this.doneChecking.systemSettings = false;
        this.doneChecking.clockDrift = false;
        this.doneChecking.vehicleCache = false;
        this.doneChecking.featureFlags = false;
    }

    checkIfDoneUpdating() {
        if (
            this.doneChecking.sync
            && this.doneChecking.appCache
            && this.doneChecking.systemSettings
            && this.doneChecking.clockDrift
            && this.doneChecking.vehicleCache
            && this.doneChecking.featureFlags
            && spin) {
            spin.stop();
            spin = null;
            this.doneChecking.checkingForUpdates = false;

            let progress = <HTMLDivElement>document.getElementById("settings-update-progress");
            progress && hide(progress);
            this.resetDoneChecking();
            let checkForUpdatesElement = document.getElementById("checkForUpdates") as HTMLInputElement;
            if (checkForUpdatesElement) {
                checkForUpdatesElement.classList.remove("disabled");
            }
            this.render();
        }
    }

    checkForUpdates() {
        this.checkIfDoneUpdating();

        if (!this.doneChecking.checkingForUpdates) {
            this.doneChecking.checkingForUpdates = true;
            spin = new Spinner().spin(this.el.querySelector("#settings-update-spinner"));
            let checkForUpdatesElement = document.getElementById("checkForUpdates") as HTMLInputElement;
            if (checkForUpdatesElement) {
                checkForUpdatesElement.classList.add("disabled");
            }
        } else {
            return;
        }

        let currentVehicle = getCurrentVehicle();

        // The forced sync below re-downloads only the current user's logs, so drop only their cached
        // historical days too (co-drivers' stay) and let the next visit rebuild them from the server.
        const currentUserId = this.app.userService.getCurrentUser()?.id;
        currentUserId && this.app.store.dispatch(HistoricalDayActions.clearUserTrackedDays(currentUserId));

        // Piggy-back bridge check on the synchronize schedule
        this.network.checkBridge();

        this.app.newsService.check().catch(handleApi);

        this.app.databaseCache.getSystemSettings()
            .then(() => {
                this.doneChecking.systemSettings = true;
                this.checkIfDoneUpdating();
                return this.app.sync.misc.syncDefectLists(true);
            })
            .catch(error => {
                this.doneChecking.systemSettings = true;
                this.checkIfDoneUpdating();
                handleApi(error);
            });

        this.app.featureFlagService.downloadFeatureFlags(this.app.api)
            .then(() => {
                this.doneChecking.featureFlags = true;
                this.checkIfDoneUpdating();
            })
            .catch(error => {
                this.doneChecking.featureFlags = true;
                this.checkIfDoneUpdating();
                handleApi(error);
            });

        this.app.databaseCache.getClockDrift()
            .then(() => {
                this.doneChecking.clockDrift = true;
                this.checkIfDoneUpdating();
            })
            .catch(error => {
                this.doneChecking.clockDrift = true;
                this.checkIfDoneUpdating();
                handleApi(error);
            });

        this.session.refreshUser(() => {
            this.sync.synchronize(true)
                .then(() => {
                    this.doneChecking.sync = true;
                    this.checkIfDoneUpdating();
                })
                .catch(error => {
                    this.doneChecking.sync = true;
                    this.checkIfDoneUpdating();
                    handleApi(error);
                });
        });

        // Get a fresh device state only if you actually have a device
        if (currentVehicle && currentVehicle.id !== "NoDeviceId") {
            this.deviceCache.fetch(this.app.api, { id: currentVehicle.id }, false)
                .then(() => {
                    this.doneChecking.vehicleCache = true;
                    this.checkIfDoneUpdating();
                })
                .catch(error => {
                    this.doneChecking.vehicleCache = true;
                    this.checkIfDoneUpdating();
                    handleApi(error);
                });
        } else {
            this.doneChecking.vehicleCache = true;
            this.checkIfDoneUpdating();
        }


        this.cacheUpdate = () => {
            // No update or update ready
            this.doneChecking.appCache = true;
            this.checkIfDoneUpdating();
        };

        this.cacheProgress = (e) => {
            let event = e.originalEvent ? e.originalEvent : e;

            if (event.loaded && event.total) {
                this.updateCacheStatus(`${ Math.round(event.loaded / event.total * 100) }%`);
            } else {
                this.updateCacheStatus();
            }
        };

        // Check service worker
        serviceWorker.update().then(this.cacheUpdate, () => {
            // No service worker, revert to AppCache
            updateAppCache();

            if (this.cacheUpdate) {
                if (!window.applicationCache || window.applicationCache.status === window.applicationCache.UNCACHED) {
                    this.cacheUpdate();
                }

                if (window.applicationCache && this.doneChecking.checkingForUpdates && !this.doneChecking.appCache) {
                    window.applicationCache.addEventListener("noupdate", this.cacheUpdate, false);
                    window.applicationCache.addEventListener("updateready", this.cacheUpdate, false);
                    window.applicationCache.addEventListener("error", this.cacheUpdate, false);
                }
            }

            if (window.applicationCache && this.cacheProgress) {
                window.applicationCache.addEventListener("progress", this.cacheProgress, false);
            }
        });
    }

    remove() {
        // clean up events
        if (this.cacheUpdate && !this.doneChecking.checkingForUpdates) {
            if (window.applicationCache) {
                window.applicationCache.removeEventListener("noupdate", this.cacheUpdate, false);
                window.applicationCache.removeEventListener("updateready", this.cacheUpdate, false);
                window.applicationCache.removeEventListener("error", this.cacheUpdate, false);
            }
            this.cacheUpdate = null;
        }

        if (this.cacheProgress) {
            window.applicationCache && window.applicationCache.removeEventListener("progress", this.cacheProgress, false);
            this.cacheProgress = null;
        }

        this.app.store.dispatch(MobileActions.setPreviousPage("other/main"));
        super.remove();
        return this;
    }

    private onUserUpdate(user): void {
        let currentUser = this.app.userService.getCurrentUser();
        if (currentUser && user && currentUser.id === user.id) {
            // Only re-render if the user changing languages is the currently active user
            this.render();
        }
    }

    private toggleDarkMode(): void {
        let nightModeToSet = !this.app.store.getState().data.nightMode;
        this.toggleSwitch.toggle(nightModeToSet);

        // Timeout allows animation of slider to play
        setTimeout(() => {
            this.app.store.dispatch(DataActions.setNightMode(nightModeToSet));
            this.render();
        }, 600);
    }

    private togglePipMode(): void {
        let pipModeToSet = !this.app.store.getState().data.pipMode;
        this.togglePipSwitch.toggle(pipModeToSet);

        // Timeout allows animation of slider to play
        setTimeout(() => {
            this.app.store.dispatch(DataActions.setPipMode(pipModeToSet));
            setPipMode(this.app.featureFlagService.hasFeatureFlag(FeatureFlagName.PIP_FUNCTIONALITY) && pipModeToSet);
            this.render();
        }, 600);
    }

    private toLogoutWorkflow(): void {
        this.app.router.toWorkflow(true);
    }

    private navigateAddin(e: Event) {
        let target = e && (e.currentTarget as HTMLLIElement);
        target && this.app.router.navigate(`addin,(addinName:${ target.id })`, true);
    }

    private navigateToPage(e: Event) {
        let target = e && (e.currentTarget as HTMLLIElement);
        let dataLink = target && target.getAttribute("data-id");
        if (!dataLink) {
            return;
        }
        // const so the non-null narrowing above survives into the closure below.
        const link = dataLink;
        if (link === "assets/main") {
            // DriveNative handles asset selection natively when its routing flag
            // is on; otherwise this falls back to the WebView page (MYG-133100).
            navigateToAssetSelection(() => this.app.router.navigate(link, true));
            return;
        }
        this.app.router.navigate(link, true);
    }

    /* eslint-disable @typescript-eslint/member-ordering */
    private getFormattedAddins(): IAddinLink[] {
        let addins = collectCustomAddins(this.app.databaseCache.getAddins(), this.app.userService.getCurrentUser());
        let formattedAddins: IAddinLink[] = [];

        // Build add-in list
        for (let i = 0; i < addins.length; i++) {
            let addin = addins[i];

            if (!(addin.path && addin.path === "DriveAppLink/")) {
                continue;
            }

            formattedAddins.push({
                id: btoa(`${ addin.addinPath }${ addin.path }`),
                name: htmlEscape(addin.menuName),
                imgUrl: addin.icon && addin.icon !== "" ? addin.icon.replace(/'/g, "&#39;") : ""
            });
        }

        return formattedAddins;
    }

}